import { CommonModule } from '@angular/common';
import { Component, computed, effect, input, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { QaiFieldComponent, QaiTextareaComponent, QaiTrimLeadingDirective, TextareaAutosizeDirective, QaiPasteCleanDirective, QaiFloatingSelectComponent, QaiFloatingSelectOption, QaiBadgeComponent } from '@qai/ui';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { DetailsSectionComponent } from 'src/app/pages/self-serve/simulation-builder/components/details-container/details-section.component';
import { AccordionCardComponent } from 'src/app/components/shared/accordion-card/accordion-card.component';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { ScoringRefinementService } from 'src/app/services/self-serve/scoring-refinement.service';
import { ActivatedRoute } from '@angular/router';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { ISimulationPrompt } from 'src/app/interfaces/self-serve/isimulation-prompt';
import { IScoreWeight } from 'src/app/interfaces/self-serve/isimulation-score';
import { IMethodology } from 'src/app/interfaces/self-serve/imethodology';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { AuthorReactiveFormService } from 'src/app/pages/self-serve/simulation-builder/services/author-reactive-form.service';
import { CallRefinementPollingService } from 'src/app/services/self-serve/call-refinement-polling.service';
import { CallRefinementStatus } from 'src/app/utils/enums/self-serve/call-refinement-status.enum';

@Component({
  selector: 'methodology-section',
  standalone: true,
  templateUrl: './methodology-section.component.html',
  styleUrls: ['./methodology-section.component.scss'],
  imports: [
    QaiFieldComponent,
    CommonModule,
    QaiTextareaComponent,
    TextareaAutosizeDirective,
    QaiTrimLeadingDirective,
    DetailsSectionComponent,
    ReactiveFormsModule,
    QaiPasteCleanDirective,
    QaiFloatingSelectComponent,
    QaiBadgeComponent,
    AccordionCardComponent,
    TranslocoModule
],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class MethodologySectionComponent {
  form!: FormGroup;
  methodologySection!: FormGroup;
  callUuid: string = '';

  isSelfAuthoring = input(false);

  readOnly = false;

  methodologyPromptLabel: string =
    'Enter the prompt that will be used to score the Methodology section of the role play. *';

  rubricOptions: QaiFloatingSelectOption[] = [];
  private methodologies: IMethodology[] = [];

  methodologyWeight = signal(0);
  selectedMethodology = signal<IMethodology | null>(null);

  /** Reactive read of the shared refinement job status — re-syncs the
   * selected methodology from the overview once a refinement started
   * elsewhere (General tab) completes, since this tab doesn't get
   * remounted when that happens. */
  private readonly pollingStatus = computed(() =>
    this.callRefinementPollingService.getStatus(this.callUuid),
  );

  onRubricChange(value: string): void {
    this.methodologyPromptGroup?.get('rubric')?.setValue(value);
    this.authoReactiveFormService.patchForm(
      this.callUuid,
      SimulationConstants.METHODOLOGY_PROMPT_PATH,
      { rubric: value }
    );
    this.authorService.selectMethodology(this.callUuid, value || null).subscribe({
      next: () => {
        if (this.isSelfAuthoring()) {
          this.scoringRefinementService.notifySaved(this.callUuid);
        }
      },
    });
    this.selectedMethodology.set(this.methodologies.find((m) => m.uuid === value) ?? null);
  }

  constructor(
    private authorService: AuthorService,
    private route: ActivatedRoute,
    private authoReactiveFormService: AuthorReactiveFormService,
    private scoringRefinementService: ScoringRefinementService,
    private callRefinementPollingService: CallRefinementPollingService
  ) {
    effect(() => {
      if (this.pollingStatus() === CallRefinementStatus.Updated && this.isSelfAuthoring()) {
        console.log('Polling status updated, refreshing selected methodology from overview...');
        this.refreshSelectedMethodologyFromOverview();
      }
    });
  }

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap.get('uuid')!;
    this.loadFormAndSetupAutoSave();
    if (this.isSelfAuthoring()) {
      this.loadMethodologyOptions();
    }
  }

  private loadMethodologyOptions(): void {
    forkJoin({
      methodologies: this.authorService.findActiveMethodologiesByAccount(this.callUuid),
      overview: this.authorService.getGeneratedCallDetails(this.callUuid),
    }).subscribe({
      next: ({ methodologies, overview }) => {
        this.methodologies = methodologies;
        this.rubricOptions = methodologies.map((m) => ({
          value: m.uuid,
          label: m.name,
        }));
        if (overview.methodologyUuid) {
          this.methodologyPromptGroup?.get('rubric')?.setValue(overview.methodologyUuid, { emitEvent: false });
          this.selectedMethodology.set(methodologies.find((m) => m.uuid === overview.methodologyUuid) ?? null);
        }
      },
    });
  }

  /** Re-fetches only the overview (not the methodology list, which a
   * refinement doesn't change) and re-applies the selected
   * methodology/rubric so a refinement that changed it is reflected
   * without a full reload. */
  private refreshSelectedMethodologyFromOverview(): void {
    if (!this.callUuid) return;

    this.authorService.getGeneratedCallDetails(this.callUuid).subscribe({
      next: (overview) => {
        const rubricControl = this.methodologyPromptGroup?.get('rubric');
        rubricControl?.setValue(overview.methodologyUuid || '', { emitEvent: false });
        this.selectedMethodology.set(
          overview.methodologyUuid
            ? this.methodologies.find((m) => m.uuid === overview.methodologyUuid) ?? null
            : null
        );
      },
    });
  }

  private setReadOnlyMode() {
    this.readOnly = this.authoReactiveFormService.isReadOnly(this.callUuid);
    this.authoReactiveFormService.setReadOnlyMode(
      this.callUuid,
      this.methodologyPromptGroup?.get('promptText')
    );
  }

  get methodologyPromptGroup(): FormGroup {
    return this.form?.get(
      SimulationConstants.METHODOLOGY_PROMPT_SECTION
    ) as FormGroup;
  }

  private loadFormAndSetupAutoSave(): void {
    this.authoReactiveFormService.getForm(this.callUuid)?.subscribe((form) => {
      this.form = form?.get('scoring') as FormGroup;
      this.methodologySection = form?.get(
        SimulationConstants.METHODOLOGY_PROMPT_PATH
      ) as FormGroup;
      this.setupAutoSave(
        this.methodologySection,
        SimulationConstants.METHODOLOGY_PROMPT_PATH
      );

      this.updateMethodologyWeight(
        this.form?.get(SimulationConstants.SCORING_WEIGHTS_SECTION)?.value
      );
      this.setupMethodologyWeightSubscription();

      this.setReadOnlyMode();
    });
  }

  private setupMethodologyWeightSubscription(): void {
    const sectionWeightsControl = this.form?.get(
      SimulationConstants.SCORING_WEIGHTS_SECTION
    );
    if (!sectionWeightsControl) return;

    sectionWeightsControl.valueChanges
      .pipe(
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe((updatedWeights: IScoreWeight[]) => {
        this.updateMethodologyWeight(updatedWeights);
      });
  }

  private updateMethodologyWeight(weights: IScoreWeight[]): void {
    this.methodologyWeight.set(
      weights?.find(
        (weight) => weight.section === SimulationConstants.METHODOLOGY_SECTION
      )?.value ?? 0
    );
  }

   private setupAutoSave(section: FormGroup, formPath: string): void {
    const promptTextControl = section?.get('promptText');

    if (!promptTextControl) return;

    let previousValue: string = promptTextControl.getRawValue();

    promptTextControl.valueChanges
      .pipe(
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe((newVal) => {
        const oldValue = previousValue;
        previousValue = newVal;

        const updatedValue = section.getRawValue();

        const trimmedOld = (oldValue || '').trim();
        const trimmedNew = (newVal || '').trim();

        if (trimmedOld === trimmedNew && trimmedNew === '') {
          return;
        }

        this.authoReactiveFormService.patchForm(
          this.callUuid,
          formPath,
          updatedValue
        );

        const readOnly = this.authoReactiveFormService.isReadOnly(
          this.callUuid
        );

        if (updatedValue && !readOnly) {
          this.triggerPromptSave(this.callUuid, updatedValue, formPath);
        }
      });
  }

  private triggerPromptSave(
    callUuid: string,
    prompt: ISimulationPrompt,
    formPath: string
  ): void {
    if (this.isSelfAuthoring()) {
      this.scoringRefinementService.notifySaved(callUuid);
      return;
    }

    const payload = { prompts: [prompt] };

    const request$ = prompt.uuid
      ? this.authorService.updateSimulationPrompts(callUuid, payload.prompts)
      : this.authorService.createSimulationPrompts(callUuid, payload.prompts);

    request$?.subscribe((response) => {
      const updatedPrompt = response?.prompts?.[0];
      if (!updatedPrompt?.uuid) return;

      this.authoReactiveFormService.patchForm(callUuid, formPath, {
        uuid: updatedPrompt.promptText === "" ? null : updatedPrompt.uuid,
      });

      this.form.updateValueAndValidity();

      if (this.isSelfAuthoring()) {
        this.scoringRefinementService.notifySaved(callUuid);
      }
    });
  }
}
